// ModalTracking.jsx
import React, {
  useEffect,
  useCallback,
  cloneElement,
  useRef,
  memo,
} from 'react';
import PropTypes from 'prop-types';

import trackMixpanel, { EventTypes, getViewId } from '../resource/mixpanel.js';
import { Accuracy } from '../selector/getTimestampOffset.js';

function ModalTracking({ children, id = '', modalId = '' }) {
  const nodeRef = useRef();
  const extraPropsRef = useRef();
  const defaultModalIdRef = useRef(
    id || children?.props?.filename || children?.type?.displayName || ''
  );
  const modalIdSourceRef = useRef(
    id
      ? 'id'
      : children?.props?.filename
        ? 'filename'
        : children?.type?.displayName
          ? 'displayname'
          : ''
  );
  const modalIdRef = useRef(modalId || '');

  useEffect(() => {
    if (!nodeRef.current) return;
    const openedTime =
      Date.now() +
      (window?.__ntp__?.getNtpTimestampOffset(Accuracy.MILLISECOND) || 0);
    const node = nodeRef.current;
    const defaultModalId = defaultModalIdRef.current;
    const modalIdSource = modalIdSourceRef.current;
    let hasSentViewOpenedEvent = false;

    if (modalIdRef.current) {
      trackMixpanel({
        type: EventTypes.VIEW_OPENED,
        payload: {
          viewId: getViewId({ pathname: window.location.pathname }),
          modalId: modalIdRef.current,
          extraProps: {
            'modal.id.source': 'modalId',
            ...extraPropsRef.current,
          },
        },
      });
      hasSentViewOpenedEvent = true;
    }

    return () => {
      if (node) {
        const closedTime =
          Date.now() +
          (window?.__ntp__?.getNtpTimestampOffset(Accuracy.MILLISECOND) || 0);
        if (!hasSentViewOpenedEvent) {
          trackMixpanel({
            type: EventTypes.VIEW_OPENED,
            payload: {
              viewId: getViewId({ pathname: window.location.pathname }),
              modalId: modalIdRef.current || defaultModalId,
              extraProps: {
                time: openedTime,
                'modal.id.source': modalIdRef.current
                  ? 'modalId'
                  : modalIdSource,
                ...extraPropsRef.current,
              },
            },
          });
        }

        trackMixpanel({
          type: EventTypes.VIEW_CLOSED,
          payload: {
            viewId: getViewId({ pathname: window.location.pathname }),
            modalId: modalIdRef.current || defaultModalId,
            extraProps: {
              $duration: (closedTime - openedTime) / 1000,
              'modal.id.source': modalIdRef.current ? 'modalId' : modalIdSource,
              ...extraPropsRef.current,
            },
          },
        });
      }
    };
  }, []);

  const setModalTracking = useCallback(({ modalId, extraProps = {} }) => {
    modalIdRef.current = modalId;
    extraPropsRef.current = extraProps;
  }, []);

  return (
    <div
      className="ModalTracking_Anchor"
      data-element_id={modalIdRef.current || defaultModalIdRef.current}
      ref={nodeRef}
    >
      {cloneElement(children, { setModalTracking })}
    </div>
  );
}

ModalTracking.propTypes = {
  children: PropTypes.node.isRequired,
  id: PropTypes.string,
  modalId: PropTypes.string,
};

export default memo(ModalTracking);
